<!-- ── Page header (title + back + cancel/save in action slot) ──────────── -->
<ui-page-header
  [title]="((canModify$ | async) ? (id ? 'companies_groups.editing_company_group' : 'companies_groups.creating_company_group') : 'companies_groups.company_group') | translate"
  [showBackButton]="true">
  <ui-button
    *ngIf="canModify$ | async"
    type="secondary"
    [label]="'common.cancel' | translate"
    (click)="cancel()">
  </ui-button>
  <ui-button
    *ngIf="canModify$ | async"
    type="primary"
    [label]="(id ? 'common.update' : 'common.save') | translate"
    [disabled]="formAddCompaniesGroups.invalid"
    (click)="save()">
  </ui-button>
</ui-page-header>

<!-- ── Form: name + description ─────────────────────────────────────────── -->
<form class="add-companies-groups__form" [formGroup]="formAddCompaniesGroups">
  <ui-form-field
    formControlName="name"
    [label]="('companies_groups.company_group_name' | translate)"
    [placeholder]="'companies_groups.company_group_name' | translate"
    class="add-companies-groups__form-field">
  </ui-form-field>
  <ui-form-field
    formControlName="description"
    [label]="('common.description' | translate)"
    [placeholder]="'common.description' | translate"
    [isOptional]="true"
    class="add-companies-groups__form-field">
  </ui-form-field>
</form>

<!-- ── Two-pane transfer ────────────────────────────────────────────────── -->
<div class="add-companies-groups__transfer">
  <!-- Left pane: Selected (N) -->
  <section class="add-companies-groups__pane">
    <header class="add-companies-groups__pane-header">
      <div class="add-companies-groups__pane-title">
        <h3>{{ 'companies_groups.included_locations' | translate }}</h3>
        <span class="add-companies-groups__count">{{ selectedLocations.length }}</span>
      </div>
      <div class="add-companies-groups__pane-toolbar">
        <ui-form-field
          [formControl]="selectedSearch"
          [prefixIcon]="'search'"
          [placeholder]="'common.search' | translate"
          class="add-companies-groups__pane-search">
        </ui-form-field>
        <ui-button *ngIf="selectedSelectedIds.length > 0 && (canModify$ | async)"
          type="danger"
          icon="trash-cc"
          iconSize="large"
          [label]="'(' + selectedSelectedIds.length + ')'"
          (click)="removeSelectedFromGroup()">
        </ui-button>
      </div>
    </header>

    <div class="add-companies-groups__table">
      <div class="add-companies-groups__table-header">
        <div class="add-companies-groups__table-cell add-companies-groups__table-cell--checkbox">
          <mat-checkbox
            *ngIf="canModify$ | async"
            [checked]="isAllSelectedSelected"
            [indeterminate]="isPartialSelectedSelected"
            [disabled]="filteredSelectedLocations.length === 0"
            (change)="toggleAllSelected($event.checked)">
          </mat-checkbox>
        </div>
        <div class="add-companies-groups__table-cell">{{ 'common.location_name' | translate }}</div>
      </div>

      <ng-container *ngIf="filteredSelectedLocations.length > 0; else selectedEmpty">
        <div *ngFor="let location of filteredSelectedLocations; trackBy: trackByFn"
          class="add-companies-groups__table-row">
          <div class="add-companies-groups__table-cell add-companies-groups__table-cell--checkbox">
            <mat-checkbox
              *ngIf="canModify$ | async"
              [checked]="isSelectedRowSelected(location.id)"
              (change)="toggleSelectedRow(location.id, $event.checked)">
            </mat-checkbox>
          </div>
          <div class="add-companies-groups__table-cell add-companies-groups__table-cell--location">
            <span class="add-companies-groups__location-name">
              {{ location.company_name + ' - ' + location.name }}
            </span>
            <span class="add-companies-groups__location-meta" *ngIf="location.apartment">
              {{ location.apartment }}
            </span>
          </div>
        </div>
      </ng-container>

      <ng-template #selectedEmpty>
        <div class="add-companies-groups__pane-empty">
          <h3>{{ 'companies_groups.drag_drop_locations_in_this_bloc' | translate }}</h3>
        </div>
      </ng-template>
    </div>
  </section>

  <!-- Right pane: Available locations -->
  <section class="add-companies-groups__pane">
    <header class="add-companies-groups__pane-header">
      <div class="add-companies-groups__pane-title">
        <h3>{{ 'companies_groups.all_locations' | translate }}</h3>
      </div>
      <div class="add-companies-groups__pane-toolbar">
        <ui-form-field
          [formControl]="search"
          [prefixIcon]="'search'"
          [placeholder]="'companies_groups.search_location' | translate"
          class="add-companies-groups__pane-search">
        </ui-form-field>
        <div class="add-companies-groups__filter" [class.is-active]="hasAvailableFilters()">
          <ui-button
            type="secondary"
            icon="filter-cc"
            iconSize="large"
            [label]="'common.filter' | translate"
            (click)="openFilterDialog()">
          </ui-button>
          <button type="button"
            *ngIf="hasAvailableFilters()"
            class="add-companies-groups__filter-clear"
            [title]="'common.clear_filters' | translate"
            (click)="clearAvailableFilters(); $event.stopPropagation()">
            <ui-svg-icon svgIcon="clear-filter" size="small"></ui-svg-icon>
          </button>
        </div>
        <ui-button *ngIf="availableSelectedIds.length > 0 && (canModify$ | async)"
          type="primary"
          icon="plus-cc"
          iconSize="large"
          [label]="('common.add' | translate) + ' (' + availableSelectedIds.length + ')'"
          (click)="addSelectedFromAvailable()">
        </ui-button>
      </div>
    </header>

    <div class="add-companies-groups__table">
      <div class="add-companies-groups__table-header">
        <div class="add-companies-groups__table-cell add-companies-groups__table-cell--checkbox">
          <mat-checkbox
            *ngIf="canModify$ | async"
            [checked]="isAllAvailableSelected"
            [indeterminate]="isPartialAvailableSelected"
            [disabled]="availableSelectableIds.length === 0"
            (change)="toggleAllAvailable($event.checked)">
          </mat-checkbox>
        </div>
        <div class="add-companies-groups__table-cell">{{ 'common.location_name' | translate }}</div>
      </div>

      <ng-container *ngIf="locations.length > 0; else availableEmpty">
        <div *ngFor="let location of locations; trackBy: trackByFn"
          class="add-companies-groups__table-row"
          [class.is-disabled]="isSelectedLocation(location)">
          <div class="add-companies-groups__table-cell add-companies-groups__table-cell--checkbox">
            <ng-container *ngIf="canModify$ | async">
              <mat-checkbox
                *ngIf="!isSelectedLocation(location)"
                [checked]="isAvailableSelected(location.id)"
                (change)="toggleAvailable(location.id, $event.checked)">
              </mat-checkbox>
            </ng-container>
            <ui-svg-icon *ngIf="isSelectedLocation(location)"
              svgIcon="check-cc" size="small"
              class="add-companies-groups__check-mark">
            </ui-svg-icon>
          </div>
          <div class="add-companies-groups__table-cell add-companies-groups__table-cell--location">
            <span class="add-companies-groups__location-name">
              {{ location.company_name + ' - ' + location.name }}
            </span>
            <span class="add-companies-groups__location-meta" *ngIf="location.apartment">
              {{ location.apartment }}
            </span>
          </div>
        </div>
      </ng-container>

      <ng-template #availableEmpty>
        <div class="add-companies-groups__pane-empty">
          <h3>{{ 'companies_groups.no_data_search' | translate }}</h3>
        </div>
      </ng-template>
    </div>

    <app-pagination
      [pagination]="pagination$ | async"
      (toPageEvent)="toPage($event)">
    </app-pagination>
  </section>
</div>
